import { makeStyles } from 'tss-react/mui';

export default makeStyles()((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 0,
    '@media print': {
      height: 'auto !important',
      overflow: 'visible !important',
      display: 'block',
    },
  },
  containerMap: {
    flexBasis: '40%',
    flexShrink: 0,
    minWidth: 0,
  },
  containerMain: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minHeight: 0,
    minWidth: 0,
    flex: 1,
    '&[data-has-map="true"]': {
      overflow: 'hidden',
    },
    '&[data-map-expanded="true"]': {
      overflow: 'hidden',
    },
    [theme.breakpoints.down('sm')]: {
      '&[data-has-map="true"]': {
        overflowY: 'auto',
        overscrollBehaviorY: 'contain',
      },
    },
    '@media print': {
      overflow: 'visible !important',
      height: 'auto !important',
      maxHeight: 'none !important',
      display: 'block',
    },
  },
  header: {
    position: 'sticky',
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius * 1.5,
    paddingBottom: theme.spacing(0.5),
  },
  columnAction: {
    width: '1%',
    paddingLeft: theme.spacing(1),
    '@media print': {
      display: 'none',
    },
  },
  filter: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 2, 2),
    '@media print': {
      display: 'none !important',
    },
  },
  filterItem: {
    minWidth: 260,
    flex: `1 1 max(${theme.dimensions.filterFormWidth}, 320px)`,
    [theme.breakpoints.down('md')]: {
      minWidth: 220,
      flex: '1 1 220px',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      flex: '1 1 100%',
    },
  },
  filterButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    flex: `1 1 ${theme.dimensions.filterFormWidth}`,
  },
  filterButton: {
    flexGrow: 1,
  },
  chart: {
    flexGrow: 1,
    overflow: 'hidden',
  },
  actionCellPadding: {
    '&.MuiTableCell-body': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '@media print': {
      display: 'none',
    },
  },
  tableCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'light'
      ? '0 10px 24px rgba(15, 23, 42, 0.06)'
      : '0 10px 24px rgba(0, 0, 0, 0.28)',
    minWidth: 0,
    maxWidth: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    isolation: 'isolate',
    '@media print': {
      overflow: 'visible !important',
      height: 'auto !important',
      maxHeight: 'none !important',
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`,
      display: 'block',
    },
  },
  tableScroller: {
    overflow: 'auto',
    flex: '1 1 auto',
    minHeight: 280,
    maxHeight: '100%',
    maxWidth: '100%',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    scrollbarGutter: 'stable both-edges',
    position: 'relative',
    '&[data-has-map="true"]': {
      minHeight: 220,
    },
    '&[data-map-expanded="true"]': {
      maxHeight: '100%',
      overflow: 'hidden',
      minHeight: 0,
      visibility: 'hidden',
      pointerEvents: 'none',
    },
    '&::-webkit-scrollbar': {
      height: 12,
      width: 12,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.mode === 'light' ? 'rgba(100, 116, 139, 0.45)' : 'rgba(148, 163, 184, 0.45)',
      borderRadius: 999,
      border: `2px solid ${theme.palette.background.paper}`,
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.mode === 'light' ? 'rgba(241, 245, 249, 0.8)' : 'rgba(51, 65, 85, 0.45)',
      borderRadius: 999,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 180,
      '&[data-has-map="true"]': {
        minHeight: 140,
      },
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      minHeight: 220,
      '&[data-has-map="true"]': {
        minHeight: 180,
      },
    },
    '@media (min-width:1600px)': {
      minHeight: 340,
      '&[data-has-map="true"]': {
        minHeight: 280,
      },
    },
    '@media print': {
      overflow: 'visible !important',
      height: 'auto !important',
      maxHeight: 'none !important',
      minHeight: 0,
      display: 'block',
      scrollbarGutter: 'auto',
    },
  },
  table: {
    minWidth: 980,
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: 'nowrap',
    },
    '& .MuiTableHead-root .MuiTableRow-root': {
      background: theme.palette.mode === 'light'
        ? 'linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(241, 245, 249, 0.96) 100%)'
        : 'linear-gradient(180deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.96) 100%)',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      fontWeight: 700,
      letterSpacing: '0.01em',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
      backgroundColor: theme.palette.action.hover,
    },
    '@media print': {
      minWidth: '0 !important',
      width: '100% !important',
      tableLayout: 'auto',
      '& .MuiTableCell-root': {
        whiteSpace: 'normal',
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        position: 'static',
      },
    },
  },
}));
